import {
  fontSize14,
  fontSize20,
} from '@aboutyou/legacy-shared/src/ui/constants/typography'
import {
  RecoSectionContent,
  TrackingPageTypeWithId,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk'
import PromotionLink from 'components/PromotionLink'
import { exists } from 'helpers/exists'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { getImageUrl } from 'helpers/image'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { RecoGridSectionTrackingContextProvider } from 'helpers/tracking/SectionTrackingContext'
import { PromotionImpression } from 'helpers/tracking/trackerComponents/ElementTracker'
import React, { FC } from 'react'
import { useService } from 'services/utils/context'
import { usePremiumPageStore } from 'store/context'
import Story from 'store/entities/Story/Story.store'
import styled from 'styled-components'
import ImageBox from 'uiComponents/atoms/ImageBox/ImageBox'
import { HeadingXL } from 'uiComponents/atoms/Text/Headings'
import { fwBold } from 'uiComponents/constants/typography'
import PremiumLandingSection from './PremiumLandingSection'

const SectionHeaderXL = styled(HeadingXL)`
  margin: 100px 0 34px 0;
`

const GridColumns = styled.div`
  text-align: center;
  flex-basis: calc(50% - 12px);
  margin-bottom: 50px;

  &:first-child:nth-last-child(5) ~ :nth-last-child(-n + 3) {
    flex-basis: calc(33.333% - 15px);
    margin-bottom: 0;
  }

  &:first-child:nth-last-child(3),
  &:first-child:nth-last-child(3) ~ :nth-last-child(-n + 3) {
    flex-basis: calc(33.333% - 15px);
    margin-bottom: 0;
  }
`

const PremiumStoryWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding-bottom: 100px;
`

const StorySubline = styled.span`
  ${fontSize14};
  display: block;
  text-align: left;
`

const StoryHeadline = styled.span`
  ${fontSize20};
  ${fwBold};
  text-align: left;
  display: block;
  padding: 27px 0 10px;
`

const ImageBoxWrapper = styled.div`
  max-height: 480px;

  img {
    display: block;
    width: 100%;
    object-fit: cover;
  }
`

const PremiumEdits: FC = () => {
  const { t } = useTranslation()
  const service = useService()
  const storyIds = usePremiumPageStore(store => store.storyIdsForEdits)

  const stories = storyIds
    .map(storyId => service.stories.getById(storyId))
    .filter(exists)

  if (stories.length < 2) {
    return null
  }

  const title = t('Premium Stories')

  return (
    <RecoGridSectionTrackingContextProvider
      headline={title}
      recoType={RecoSectionContent.storyRecommendationCategory}
    >
      <PremiumLandingSection
        sectionTitle={<SectionHeaderXL>{title}</SectionHeaderXL>}
      >
        <PremiumStoryWrapper data-testid="PremiumStories">
          {stories.map(story => {
            return <TrackedStoryTile key={story.id} story={story} />
          })}
        </PremiumStoryWrapper>
      </PremiumLandingSection>
    </RecoGridSectionTrackingContextProvider>
  )
}

export default PremiumEdits

const TrackedStoryTile: FC<{ story: Story }> = ({ story }) => {
  const imageUrl = getImageUrl(story.image, {
    trim: false,
    width: 800,
    height: 600,
    bg: 'f2f2f2',
  })

  if (!imageUrl) {
    return null
  }
  return (
    <ElementTrackingContextProvider>
      <PromotionImpression<HTMLDivElement>
        type={TrackingPageTypeWithId.Story}
        id={story.id}
        name={story.headline}
        url={story.url}
        source={TrackingPromotionCreative.StoryReco}
        useIdForDestination
      >
        {({ ref, onClickCapture }) => (
          <GridColumns>
            <PromotionLink onClickCapture={onClickCapture} to={story.url}>
              <div ref={ref} data-testid="PremiumStoryTile">
                <ImageBoxWrapper>
                  <ImageBox src={imageUrl} />
                </ImageBoxWrapper>

                <StoryHeadline>{story.headline}</StoryHeadline>
                <StorySubline>{story.subline}</StorySubline>
              </div>
            </PromotionLink>
          </GridColumns>
        )}
      </PromotionImpression>
    </ElementTrackingContextProvider>
  )
}
